import { db } from '@/shared/db/database';
import { FaceEmbedding } from '@/modules/attendance/domain/valueObjects/FaceEmbedding';
import { LocalFaceEmbeddingRepository } from '@/modules/attendance/infrastructure/repositories/LocalFaceEmbeddingRepository';
import { FACE_ENROLL_DUPLICATE_THRESHOLD_COSINE } from '@/shared/config/terminal.config';

const localRepo = new LocalFaceEmbeddingRepository();

export interface DuplicateFaceCheckResult {
  conflict: boolean;
  conflictingIdPerson?: number | undefined;
  conflictingFullName?: string | undefined;
  similarity?: number | undefined;
}

/**
 * Pre-checks all stored embeddings for the given accessRoleId to detect whether the
 * candidate embedding already belongs to a DIFFERENT person.
 *
 * - Skips the row whose `idPerson === excludeIdPerson` (re-enrollment safety).
 * - Skips rows whose `dim !== 512` or whose vector fails `FaceEmbedding.from()` (malformed guard).
 * - Returns on the FIRST conflict found (≥ FACE_ENROLL_DUPLICATE_THRESHOLD_COSINE).
 * - Never throws — malformed rows are caught per-row (mirrors FaceMatchingRepository.match L84).
 */
export async function checkDuplicateFaceEnrollmentUseCase(
  embedding: FaceEmbedding,
  accessRoleId: number,
  excludeIdPerson: number,
): Promise<DuplicateFaceCheckResult> {
  const rows = await localRepo.getByAccessRole(accessRoleId);

  for (const rec of rows) {
    if (rec.idPerson === excludeIdPerson) continue;

    try {
      const stored = FaceEmbedding.from(rec.embedding);
      const sim = embedding.cosineSimilarity(stored);

      if (sim >= FACE_ENROLL_DUPLICATE_THRESHOLD_COSINE) {
        const empRow = await db.employees.get(rec.idPerson);
        const raw = (empRow?.raw ?? {}) as Record<string, unknown>;
        const fullName =
          `${String(raw['first_name'] ?? '')} ${String(raw['last_name1'] ?? '')}`.trim() ||
          undefined;

        return {
          conflict: true,
          conflictingIdPerson: rec.idPerson,
          conflictingFullName: fullName,
          similarity: sim,
        };
      }
    } catch {
      /* malformed row — skip, mirrors FaceMatchingRepository.match L84 */
    }
  }

  return { conflict: false };
}
